<template>
    <div class="ebook-activity">
        <div v-if="activityContent.content" class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content || ''" />
                </e-field>
            </div>
        </div>

        <!-- eBook Display Cards -->
        <div v-for="(ebook, index) in ebooks" :key="`ebook-${index}`" class="ebook-card-wrap">
            <div class="ebook-card">
                <div class="ebook-card-body">
                    <figure class="ebook-cover-figure">
                        <img
                            :src="ebook?.ebook?.cover_url || '/placeholder-book-cover.jpg'"
                            :alt="`${displayEbookHeading(ebook)} cover`"
                            class="ebook-cover-img"
                        />
                    </figure>

                    <div class="ebook-meta">
                        <p class="ebook-meta-row">
                            <span class="ebook-meta-label">Title:</span>
                            <span class="ebook-meta-value">{{ displayEbookHeading(ebook) }}</span>
                        </p>
                        <p v-if="ebook?.ebook?.author" class="ebook-meta-row">
                            <span class="ebook-meta-label">Author:</span>
                            <span class="ebook-meta-value">{{ ebook.ebook.author }}</span>
                        </p>
                        <p v-if="ebookChapter(ebook) || ebookPage(ebook)" class="ebook-meta-row ebook-meta-row--inline">
                            <span v-if="ebookChapter(ebook)" class="ebook-meta-inline">
                                <span class="ebook-meta-label">Chapter:</span>
                                <span class="ebook-meta-value">{{ ebookChapter(ebook) }}</span>
                            </span>
                            <span v-if="ebookPage(ebook)" class="ebook-meta-inline">
                                <span class="ebook-meta-label">Page:</span>
                                <span class="ebook-meta-value">{{ ebookPage(ebook) }}</span>
                            </span>
                        </p>
                        <p v-if="ebookYear(ebook)" class="ebook-meta-row">
                            <span class="ebook-meta-label">Year:</span>
                            <span class="ebook-meta-value">{{ ebookYear(ebook) }}</span>
                        </p>
                        <p v-if="ebook?.ebook?.edition" class="ebook-meta-row">
                            <span class="ebook-meta-label">Version:</span>
                            <span class="ebook-meta-value">{{ ebook.ebook.edition }}</span>
                        </p>
                        <p v-if="(ebook?.ebook?.format) || (ebook?.format)" class="ebook-meta-row">
                            <span class="ebook-meta-label">Format:</span>
                            <span class="ebook-meta-value">{{ ebook?.ebook?.format || ebook?.format }}</span>
                        </p>
                        <p v-if="ebook?.ebook?.description" class="ebook-meta-row ebook-meta-row--description">
                            <span class="ebook-meta-label">Description:</span>
                            <span class="ebook-meta-value">{{ ebook.ebook.description }}</span>
                        </p>
                        <p
                            v-else-if="shouldShowLearnerDeepLinkSubtitle(ebook) && !ebookChapter(ebook)"
                            class="ebook-meta-row"
                        >
                            <span class="ebook-meta-value">{{ ebook.deep_link_title }}</span>
                        </p>
                    </div>
                </div>

                <div class="ebook-card-actions">
                    <div
                        v-if="isOptedOut(ebook)"
                        class="ebook-opted-out tw:flex tw:items-center tw:gap-2 tw:text-sm tw:whitespace-nowrap"
                    >
                        <e-icon icon="info-circle" size="is-small" type="is-info" />
                        <span>You declined this Ebook</span>
                    </div>

                    <e-button
                        :type="!isOptedOut(ebook) ? 'is-primary' : ''"
                        :class="{ 'opted-out-button-outlined': isOptedOut(ebook) }"
                        outlined
                        size="is-small"
                        icon-left="eye"
                        :disabled="isOptedOut(ebook)"
                        @click="!isOptedOut(ebook) && readFromBeginning(ebook)"
                    >
                        Read from the Beginning
                    </e-button>

                    <e-button
                        :type="!isOptedOut(ebook) ? 'is-primary' : ''"
                        :class="{ 'opted-out-button': isOptedOut(ebook) }"
                        size="is-small"
                        icon-left="eye"
                        :disabled="isOptedOut(ebook)"
                        @click="!isOptedOut(ebook) && continueReading(ebook)"
                    >
                        Continue Reading
                    </e-button>
                </div>
            </div>
        </div>

        <!-- Late Notice -->
        <div v-if="isLate && !completed && !userHasAuthorizedAttempt" class="columns">
            <div class="column">
                <late-notice :can-submit-late="canSubmitLate" />
            </div>
        </div>

        <!-- Attempt Authorization Notice -->
        <attempt-authorization-notice
            v-if="hasAnyAttemptAuthorizations"
            :attempt-authorizations="userUnusedAuthorizedAttempts"
        />

        <!-- ebook-preview-drawer (only mount when a valid resource id exists) -->
        <ebook-preview-drawer
            v-if="ltiResourceLinkId !== null && ltiResourceLinkId !== ''"
            :show-ebook-preview-drawer="showEbookPreviewDrawer"
            :selected-ebook-title="selectedEbookTitle"
            :selected-ebook-format="selectedEbookFormat"
            :lti-resource-link-id="ltiResourceLinkId"
            :has-specific-location="hasSpecificLocation"
            :continue-reading="isContinueReading"
            :vital-source-info-loaded="vitalSourceInfoLoaded"
            @close="showEbookPreviewDrawer = false"
        />

        <activity-completion-action-row class="tw:mt-3">
            <e-button
                v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                :loading="isLoading"
                :disabled="disabled"
                type="is-primary"
                @click="submit"
                @keyup="submit"
            >
                Mark Complete
            </e-button>
        </activity-completion-action-row>

        <div v-if="!readOnly && canViewSubmissions" class="columns mt-3">
            <div class="column">
                <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading" />
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <attachments :media="media" :namespace="namespace" :uuid="uuid" />
            </div>
        </div>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import LateNotice from '@/components/activity-types/course-activity-types/LateNotice.vue';
import AttemptAuthorizationNotice from '@/components/attempt-authorizations/AttemptAuthorizationNotice.vue';
import EbookPreviewDrawer from '@/components/ebook/EbookPreviewDrawer.vue';
import { shouldShowDistinctDeepLinkTitle } from '@/components/ebook/ebookDisplayHelpers.js';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import { useActivity } from '@/composables/useActivity.js';

export default {
    name: 'Ebook',

    components: {
        ActivityCompletionActionRow,
        LateNotice,
        AttemptAuthorizationNotice,
        EbookPreviewDrawer,
        DisplayContent,
        Submissions,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    data() {
        return {
            showEbookPreviewDrawer: false,
            selectedEbookTitle: '',
            selectedEbookFormat: '',
            ltiResourceLinkId: null,
            optedOutEbooks: new Set(),
            hasSpecificLocation: false,
            isContinueReading: false,
            vitalSourceInfoLoaded: false,
        };
    },

    computed: {
        ebooks() {
            return this.activityContent.active_lti_ebooks ?? [];
        },

        canSubmit() {
            if (this.userHasAuthorizedAttempt) {
                return true;
            }

            if (!this.completed && !this.readOnly) {
                if (this.isLate && !this.canSubmitLate) {
                    return false;
                }
                return true;
            }

            return false;
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.course ? this.course.id : this.$route.params.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
            };
        },
    },

    async mounted() {
        try {
            await this.$store.dispatch('Integrations/getVitalSourceRelatedInfo');
            this.vitalSourceInfoLoaded = true;
        } catch {
            this.showEbookPreviewDrawer = false;
            this.ltiResourceLinkId = null;
            this.hasSpecificLocation = false;
            this.isContinueReading = false;
        }
    },

    methods: {
        displayEbookHeading(ebook) {
            const fromEbook = ebook?.ebook?.title;
            if (typeof fromEbook === 'string' && fromEbook.trim() !== '') {
                return fromEbook.trim();
            }
            const activityTitle = this.get('title');
            if (activityTitle != null && String(activityTitle).trim() !== '') {
                return String(activityTitle).trim();
            }
            return 'Unknown';
        },

        shouldShowLearnerDeepLinkSubtitle(ebook) {
            return shouldShowDistinctDeepLinkTitle(ebook);
        },

        ebookChapter(ebook) {
            const explicit = ebook?.ebook?.chapter ?? ebook?.chapter;
            if (explicit != null && String(explicit).trim() !== '') {
                return String(explicit).trim();
            }

            const deep = String(ebook?.deep_link_title ?? '').trim();
            const match = deep.match(/^chapter\s*:?\s*(.+)$/i);
            return match ? match[1].trim() : null;
        },

        ebookPage(ebook) {
            const page = ebook?.ebook?.page ?? ebook?.page;
            if (page == null || String(page).trim() === '') {
                return null;
            }
            return String(page).trim();
        },

        ebookYear(ebook) {
            const year = ebook?.ebook?.year ?? ebook?.ebook?.copyright_year ?? ebook?.year;
            if (year == null || String(year).trim() === '') {
                return null;
            }
            return String(year).trim();
        },

        isOptedOut(ebook) {
            if (this.optedOutEbooks.has(ebook.id)) {
                return true;
            }
            if (Array.isArray(ebook.enrollments)) {
                return ebook.enrollments.some(e => e.opted_out_at);
            }
            return false;
        },

        buildEbookUrl(ltiResourceLinkId, params = {}) {
            const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
            const queryParams = new URLSearchParams();

            if (params.has_specific_location !== undefined) {
                queryParams.append('has_specific_location', params.has_specific_location);
            }
            if (params.continue_reading !== undefined) {
                queryParams.append('continue_reading', params.continue_reading);
            }
            if (params.format) {
                queryParams.append('format', params.format);
            }

            const queryString = queryParams.toString();
            return `${baseUrl}/lti/links/${ltiResourceLinkId}${queryString ? `?${queryString}` : ''}`;
        },

        openEbookInNewTab(url) {
            try {
                this.$edverumToast.open({
                    message: 'Opening your Ebook in a new tab…',
                    type: 'is-info',
                    duration: 2000,
                });

                window.open(url, '_blank', 'noopener,noreferrer');
            } catch (error) {
                this.$edverumToast.open({
                    message: 'Your browser blocked the new tab. Please allow pop-ups for this site and try again.',
                    type: 'is-warning',
                    duration: 5000,
                });
            }
        },

        readFromBeginning(ebook) {
            if (!ebook?.lti_resource_link_id) {
                this.$edverumToast.open({ message: 'No Ebook URL available', type: 'is-info' });
                return;
            }

            const openInNewTab = import.meta.env.VITE_APP_EBOOK_OPEN_IN_NEW_TAB;

            this.$dialog.confirm({
                title: 'Read from the beginning',
                message:
                    'Please note that using this Ebook launch option will reset your last reading position in this Ebook.',
                confirmText: 'Continue',
                cancelText: 'Cancel',
                type: 'is-info',
                onConfirm: () => {
                    if (openInNewTab === 'true') {
                        const url = this.buildEbookUrl(ebook.lti_resource_link_id, {
                            has_specific_location: ebook.has_specific_location || false,
                            continue_reading: false,
                            format: ebook?.ebook?.format || '',
                        });
                        this.openEbookInNewTab(url);
                    } else {
                        this.ltiResourceLinkId = ebook.lti_resource_link_id;
                        this.selectedEbookTitle = this.displayEbookHeading(ebook);
                        this.selectedEbookFormat = ebook?.ebook?.format || '';
                        this.hasSpecificLocation = ebook.has_specific_location || false;
                        this.isContinueReading = false;
                        this.showEbookPreviewDrawer = true;
                    }
                },
            });
        },

        continueReading(ebook) {
            if (!ebook?.lti_resource_link_id) {
                this.$edverumToast.open({ message: 'No Ebook URL available', type: 'is-info' });
                return;
            }

            const openInNewTab = import.meta.env.VITE_APP_EBOOK_OPEN_IN_NEW_TAB;

            if (openInNewTab === 'true') {
                const url = this.buildEbookUrl(ebook.lti_resource_link_id, {
                    has_specific_location: ebook.has_specific_location || false,
                    continue_reading: true,
                    format: ebook?.ebook?.format || '',
                });
                this.openEbookInNewTab(url);
            } else {
                this.ltiResourceLinkId = ebook.lti_resource_link_id;
                this.selectedEbookTitle = this.displayEbookHeading(ebook);
                this.selectedEbookFormat = ebook?.ebook?.format || '';
                this.hasSpecificLocation = ebook.has_specific_location || false;
                this.isContinueReading = true;
                this.showEbookPreviewDrawer = true;
            }
        },
    },
};
</script>

<style scoped>
.ebook-card-wrap {
    margin-bottom: 1.25rem;
}

.ebook-card {
    background: var(--p-content-background);
    border: 1px solid var(--p-content-border-color);
    border-radius: 10px;
    box-shadow: 0 2px 8px color-mix(in srgb, var(--p-text-color) 8%, transparent);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    color: var(--p-text-color);
}

.ebook-card-body {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;
}

.ebook-cover-figure {
    width: 128px;
    height: 184px;
    margin: 0;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 8px;
    background: var(--p-surface-100, #f1f5f9);
}

.ebook-cover-img {
    width: 128px;
    height: 184px;
    object-fit: cover;
    object-position: center;
    display: block;
}

.ebook-meta {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.ebook-meta-row {
    margin: 0;
    line-height: 1.45;
    font-size: 0.95rem;
}

.ebook-meta-row--inline {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.ebook-meta-inline {
    display: inline-flex;
    gap: 0.35rem;
    align-items: baseline;
}

.ebook-meta-label {
    font-weight: 700;
    color: var(--p-text-color);
    margin-right: 0.3rem;
}

.ebook-meta-value {
    color: var(--p-text-muted-color);
    font-weight: 400;
    overflow-wrap: break-word;
    word-wrap: break-word;
}

.ebook-meta-row--description .ebook-meta-value {
    color: var(--p-text-muted-color);
}

.ebook-card-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.ebook-opted-out {
    margin-right: auto;
    color: var(--p-text-muted-color);
}

/* Opted-out button states */
.opted-out-button-outlined {
    background-color: transparent;
    color: var(--p-text-muted-color);
    border-color: var(--p-content-border-color);
    cursor: not-allowed;
}

.opted-out-button-outlined:hover {
    background-color: transparent;
    color: var(--p-text-muted-color);
    border-color: var(--p-content-border-color);
}

.opted-out-button {
    background-color: var(--p-surface-400, #b5b5b5);
    color: var(--p-primary-contrast-color, #fff);
    border-color: var(--p-surface-400, #b5b5b5);
    cursor: not-allowed;
}

.opted-out-button:hover {
    background-color: var(--p-surface-400, #b5b5b5);
    color: var(--p-primary-contrast-color, #fff);
    border-color: var(--p-surface-400, #b5b5b5);
}

@media (max-width: 640px) {
    .ebook-card {
        padding: 1rem;
    }

    .ebook-card-body {
        gap: 0.85rem;
    }

    .ebook-cover-figure {
        width: 96px;
        height: 96px;
    }

    .ebook-cover-img {
        width: 96px;
        height: 138px;
    }

    .ebook-meta-row {
        font-size: 0.9rem;
    }

    .ebook-card-actions {
        flex-direction: column;
        align-items: stretch;
    }

    /* Target PrimeVue root button class, native buttons and anchor-buttons
       so all `e-button` renders receive consistent sizing/alignment. */
    .ebook-card-actions .p-button,
    .ebook-card-actions button,
    .ebook-card-actions a.p-button {
        width: 100%;
        justify-content: center;
        min-height: 44px;
    }

    .ebook-opted-out {
        margin-right: 0;
        margin-bottom: 0.25rem;
    }
}

@media (max-width: 414px) {
    .ebook-cover-figure {
        width: 80px;
        height: 80px;
    }

    .ebook-cover-img {
        width: 80px;
        height: 115px;
    }

    .ebook-meta-row {
        font-size: 0.85rem;
    }
}
</style>
